import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * =========================
 * FETCH DASHBOARD STATS
 * =========================
 */
export const fetchDashboardStats = createAsyncThunk(
  "dashboard/fetchDashboardStats",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth?.token;

      const res = await axios.get(`${API_URL}/api/orders`, {
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : {},
      });

      const orders = res.data.data;

      // Get today's date at midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get first day of current month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      firstDayOfMonth.setHours(0, 0, 0, 0);

      // Filter today's orders
      const todayOrders = orders.filter((order) => {
        const orderDate = new Date(order.createdAt);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime();
      });

      // Filter this month's orders
      const thisMonthOrders = orders.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= firstDayOfMonth;
      });

      // Calculate today's stats
      const todayStats = {
        revenue: todayOrders.reduce((sum, order) => sum + order.totalAmount, 0),
        profit: todayOrders.reduce((sum, order) => {
          // Assuming profit is calculated as revenue - discount - some cost
          // Adjust this calculation based on your business logic
          const profit = order.totalAmount - order.discount;
          return sum + profit;
        }, 0),
        ordersReceived: todayOrders.length,
        ordersCancelled: todayOrders.filter((o) => o.status === "cancelled").length,
        ordersAccepted: todayOrders.filter((o) => o.orderStatus === 2 || o.orderStatus === 3).length,
        ordersOnTheWay: todayOrders.filter((o) => o.orderStatus === 4).length,
        ordersDelivered: todayOrders.filter((o) => o.orderStatus === 5).length,
      };

      // Calculate this month's stats
      const thisMonthStats = {
        revenue: thisMonthOrders.reduce((sum, order) => sum + order.totalAmount, 0),
        profit: thisMonthOrders.reduce((sum, order) => {
          const profit = order.totalAmount - order.discount;
          return sum + profit;
        }, 0),
        ordersReceived: thisMonthOrders.length,
        ordersCancelled: thisMonthOrders.filter((o) => o.status === "cancelled").length,
        ordersAccepted: thisMonthOrders.filter((o) => o.orderStatus === 2 || o.orderStatus === 3).length,
        ordersOnTheWay: thisMonthOrders.filter((o) => o.orderStatus === 4).length,
        ordersDelivered: thisMonthOrders.filter((o) => o.orderStatus === 5).length,
      };

      // Calculate total stats (all time)
      const totalStats = {
        revenue: orders.reduce((sum, order) => sum + order.totalAmount, 0),
        profit: orders.reduce((sum, order) => {
          const profit = order.totalAmount - order.discount;
          return sum + profit;
        }, 0),
        ordersReceived: orders.length,
        ordersCancelled: orders.filter((o) => o.status === "cancelled").length,
        ordersDelivered: orders.filter((o) => o.orderStatus === 5).length,
      };

      return {
        today: todayStats,
        thisMonth: thisMonthStats,
        total: totalStats,
        allOrders: orders,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch dashboard stats"
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    todayStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersAccepted: 0,
      ordersOnTheWay: 0,
      ordersDelivered: 0,
    },
    thisMonthStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersAccepted: 0,
      ordersOnTheWay: 0,
      ordersDelivered: 0,
    },
    totalStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersDelivered: 0,
    },
    loading: false,
    error: null,
  },
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.todayStats = action.payload.today;
        state.thisMonthStats = action.payload.thisMonth;
        state.totalStats = action.payload.total;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;